import { ChangeEvent, useContext, useEffect, useState } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { KeylessArea, showEditKeylessOperationAreaState } from "./KeylessOperationArea";
import { useForm, useFormState } from "react-hook-form";
import { FormNumberInput, FormTextField } from "../../../common/form-fields-wrappers";
import { useRecoilState, useSetRecoilState } from "recoil";
import { keylessAreaState, keylessErrorState } from "../../../search-dms/state";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { SET_SNACKBAR } from "../../../common/utils";
import { updateKeylessOperationArea } from "../../../search-dms/dmsSearch.actions";
import CustomMap from "./KeylessAreaMap";
import _ from "underscore";

type KeylessOperationAreaEditFormPopupProps = {
  keylessArea: KeylessArea;
  onChange: (index: number, field: string, value: any) => void;
}

const KeylessOperationAreaEditFormPopup = (props: KeylessOperationAreaEditFormPopupProps) => {
  const { t } = useTranslation(["network", "translation"]);
  var keylessArea = props.keylessArea;

  const [showEdit, setShowEdit] = useRecoilState(showEditKeylessOperationAreaState);

  const [triggerEffect, setTriggerEffect] = useState(false); //Used to force the call of the useEffect

  /// Fields with errors
  const [fieldsWithErrors, setFieldsWithErrors] = useState({} as { [k: string]: boolean });

  const isFloat = (v: any) => /^-?\d+(\.\d+)?$/.test(v?.toString());
  const isPositiveInt = (v: any) => /^\d+$/.test(v?.toString());

  const fieldIsEmpty = (name: keyof typeof keylessArea): boolean => {
    return (keylessArea[name] === undefined)
      || (keylessArea[name] === null)
      || (keylessArea[name] === '');
  }

  useEffect(() => {
    const fieldsWithErrorsList = Object.keys(keylessArea).filter(fieldIsEmpty as (v: string) => boolean);

    if (!isFloat(keylessArea.lat)) {
      fieldsWithErrorsList.push("lat");
    }

    if (!isFloat(keylessArea.lon)) {
      fieldsWithErrorsList.push("lon");
    }

    if (!isPositiveInt(keylessArea.radius)) {
      fieldsWithErrorsList.push("radius");
    }

    if (fieldsWithErrorsList.includes("id")) {
      const idIdx = fieldsWithErrorsList.findIndex(v => v === "id");
      fieldsWithErrorsList.splice(idIdx, 1);
    }

    setFieldsWithErrors(Object.fromEntries(fieldsWithErrorsList.map((field: string) => [field, true])));
  }, [keylessArea]);


  /// End of fields with errors

  /// handleChangeCircleValues
  const [latitude, setLatitude] = useState(keylessArea?.lat ?? (keylessArea?.lat != null ? keylessArea.lat : ''));
  const [longitude, setLongitude] = useState(keylessArea?.lon ?? (keylessArea?.lon != null ? keylessArea.lon : ''));
  const [radius, setRadius] = useState(keylessArea?.radius ?? 1000);

  function isNumberWithOptionalDecimal(value: string) {
    const regex = /^-?\d+(\.\d+)?(\.)?$/;
    return regex.test(value);
  }

  function isNumber(value: string) {
    const regex = /^\d+$/;
    return regex.test(value);
  }

  function checkLongitudeMax(value: number) {
    return value >= -180 && value <= 180;
  }

  function checkLatitudeMax(value: number) {
    return value >= -90 && value <= 90;
  }

  function checkRadiusMax(value: number) {
    return value >= 0 && value <= 1000000;
  }

  const handleChangeCircleValues = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | Number, fieldName: string) => {
    //console.log("handleChangeLat --> fieldName: " + fieldName);
    const valueStr = (() => {
      if (typeof e === "number") return e.toString();
      return (e as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>).target.value;
    })();
    //console.log("handleChangeLat --> valueStr: " + valueStr);

    let newValue;
    let checkValue = false;
    let isRadius = false;

    //console.log("handleChangeLat --> fieldName: " + fieldName);
    //Check on the input: if number or decimal
    if (fieldName === "radius") {
      isRadius = true;
      checkValue = isNumber(valueStr) && checkRadiusMax(parseInt(valueStr));
    } else {
      //lat, lon
      checkValue = isNumberWithOptionalDecimal(valueStr);
    }

    //console.log("handleChangeLat --> checkValue: " + checkValue);
    if (checkValue) {
      if (isRadius) {
        newValue = parseInt(valueStr);
        setRadius(newValue);
        setTriggerEffect(true);
      } else {
        newValue = parseFloat(valueStr);
        if (!valueStr.endsWith('0') && !valueStr.endsWith('.')) {
          if (fieldName === "lat") {
            if (checkLatitudeMax(newValue)) {
              setLatitude(newValue);
              setTriggerEffect(true);
            }
          } else {
            // Lon
            if (checkLongitudeMax(newValue)) {
              setLongitude(newValue);
              setTriggerEffect(true);
            }
          }
        }
      }
      // setTriggerEffect(true);
    }

    //If the value is not valid: remove the focus and start the useEffect to reset the value
    /*
    console.log("handleChangeLat --> Not Valid Number!");
    if (Object.prototype.hasOwnProperty.call(e, "target")) {
      (e as any).target.blur(); // Remove focus
    }
    setTriggerEffect(true); //Used to force the call of the useEffect
    */

    //TODO some kind of error message to the user??
  };

  /// Refresh
  useEffect(() => {
    //console.log("useEffect --> keylessArea: " + JSON.stringify(keylessArea));
    setTriggerEffect(false); //Used to force the call of the useEffect

    keylessOperationAreaForm.resetField("lat", { defaultValue: latitude });
    keylessOperationAreaForm.resetField("lon", { defaultValue: longitude });
    keylessOperationAreaForm.resetField("radius", { defaultValue: radius });
  }, [
    triggerEffect,
    latitude,
    longitude,
    radius,
    keylessArea,
  ]);
  ///


  function onMapClick() {
    return (e: google.maps.MapMouseEvent) => {

      console.log("map mouse", e.latLng?.lat(), e.latLng?.lng());
      //keylessArea.lat = e.latLng?.lat();
      //keylessArea.lon = e.latLng?.lng();
      setLatitude(e.latLng?.lat() ?? 0);
      setLongitude(e.latLng?.lng() ?? 0);
    };
  }

  const keylessOperationAreaForm = useForm({
    mode: "onChange",
    defaultValues: {
      id: keylessArea?.id,
      keyLessInfo: keylessArea?.keyLessInfo ?? "",
      lat: keylessArea?.lat ?? 0,
      lon: keylessArea?.lon ?? 0,
      radius: keylessArea?.radius ?? 10, // default radius
      mobilityStoreId: keylessArea?.mobilityStoreId,
    },
  });

  /// Save
  const { errors: formErrors } = useFormState(keylessOperationAreaForm);
  const formHasErrors = !_.isEmpty(formErrors);

  const [keylessAreas, setKeylessAreas] = useRecoilState(keylessAreaState);
  const setKeylessError = useSetRecoilState(keylessErrorState);

  const { dispatchGlobalData } = useContext(GlobalContext);

  function convertFormData(data: any) {
    const defaultValues = keylessOperationAreaForm.formState.defaultValues!;

    const fieldIsEmpty = (name: keyof typeof defaultValues): boolean => {
      return (data[name] === undefined)
        || (data[name] === null)
        || (data[name] === '');
    }

    const isFloat = (v: any) => /^-?\d+(\.\d+)?$/.test(v?.toString());
    const isPositiveInt = (v: any) => /^\d+$/.test(v?.toString());

    const fieldsWithErrorsList = Object.keys(defaultValues).filter(fieldIsEmpty as (v: string) => boolean);

    if (!isFloat(data.lat)) {
      fieldsWithErrorsList.push("lat");
    }

    if (!isFloat(data.lon)) {
      fieldsWithErrorsList.push("lon");
    }

    if (!isPositiveInt(data.radius)) {
      fieldsWithErrorsList.push("radius");
    }

    if (fieldsWithErrorsList.includes("id")) {
      const idIdx = fieldsWithErrorsList.findIndex(v => v === "id");
      fieldsWithErrorsList.splice(idIdx, 1);
    }

    setFieldsWithErrors(Object.fromEntries(fieldsWithErrorsList.map((field: string) => [field, true])));

    if ((formHasErrors || fieldsWithErrorsList.length)) {
      console.log({ fieldsWithErrorsList, formHasErrors })
      return null;
    }

    return {
      id: keylessArea?.id,
      keyLessInfo: data?.keyLessInfo,
      lat: data?.lat,
      lon: data?.lon,
      radius: data?.radius,
      mobilityStoreId: keylessArea?.mobilityStoreId
        ? keylessArea?.mobilityStoreId
        : keylessArea.id, // TODO: If is new, what?
    };
  }

  const handleSave = (data: any) => {
    if (formHasErrors) {
      dispatchGlobalData(SET_SNACKBAR.ERROR);
      return;
    }

    const reqParameters = convertFormData(data);
    if (!reqParameters) return; // Error was thrown
    console.log(
      "updateKeylessOperationArea --> res :" + JSON.stringify(reqParameters)
    );

    updateKeylessOperationArea(reqParameters)
      .then((res: any) => {
        console.log("updateKeylessOperationArea --> res :" + JSON.stringify(res));

        setKeylessAreas((prev: any) => {
          const exists = prev.some((area: any) => area.id === res.msKeylessArea.id);
          if (exists) {
            // Editing
            return prev.map((area: any) =>
              area.id === res.msKeylessArea.id ? res.msKeylessArea : area
            );
          } else {
            // Adding
            return [...prev, res.msKeylessArea];
          }
        });

        dispatchGlobalData(SET_SNACKBAR.SUCCESS);
      })
      .catch((err: any) => {
        setKeylessError(err);
      });
    setShowEdit(false);
  };

  return <>
    <DialogTitle>{(keylessArea.id !== -1) ? t("dmsDetail.keyless.operationAreaTab.dialogEdit.title") : t("dmsDetail.keyless.operationAreaTab.dialogAdd.title")}</DialogTitle>
    <DialogContent dividers>
      <FormTextField
        error={!!fieldsWithErrors.keyLessInfo}
        control={keylessOperationAreaForm.control}
        name="keyLessInfo"
        label={t("dmsDetail.keyless.operationAreaTab.keyLessInfo")}
        multiline
        rows={3}
        fullWidth
        InputProps={{
          style: {
            border: "2px dashed #ccc",
            borderRadius: "5px",
          },
        }}
      />

      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        alignItems="flex-end"
      >
        <FormTextField
          error={!!fieldsWithErrors.lat}
          control={keylessOperationAreaForm.control}
          name="lat"
          label={t("dmsDetail.keyless.operationAreaTab.latitude")}
          onChange={(e) => handleChangeCircleValues(e, "lat")}
        />
        <FormTextField
          error={!!fieldsWithErrors.lat}
          control={keylessOperationAreaForm.control}
          name="lon"
          label={t("dmsDetail.keyless.operationAreaTab.longitude")}
          onChange={(e) => handleChangeCircleValues(e, "lon")}
        />
        <FormNumberInput
          error={!!fieldsWithErrors.radius}
          control={keylessOperationAreaForm.control}
          name="radius"
          label={t("dmsDetail.keyless.operationAreaTab.radius")}
          onChange={(e: number) => handleChangeCircleValues(e, "radius")}
          defaultValue={radius}
          minValue={0}
          maxValue={1000000}
        />
      </Stack>
      <CustomMap latitude={latitude || 0} longitude={longitude || 0} radius={radius} onClick={onMapClick()} />
    </DialogContent>
    <DialogActions>
      <Button onClick={() => setShowEdit(false)}>{t("rsDetail.documentRepositoryDms.dialog.action.cancel")}</Button>
      <Button variant="contained" onClick={keylessOperationAreaForm.handleSubmit(handleSave)}>{t("rsDetail.documentRepositoryDms.dialog.action.save")}</Button>
    </DialogActions>
  </>
}

export default KeylessOperationAreaEditFormPopup;
